import { useEffect } from 'react';

// This guard is only for accidental double taps.  Keeping a button locked for
// the duration of a network request made ordinary actions feel unresponsive
// (and could leave them unavailable for 12 seconds after a failed request).
const CLICK_GUARD_MS = 700;
const ACTION_BUTTON_SELECTOR = [
  'button',
  'a[role="button"]',
  'a.btn-link',
  'a.btn',
  '.ant-btn',
  '.MuiButtonBase-root'
].join(',');

const PASSIVE_BUTTON_TYPES = new Set(['button', 'reset']);
const PASSIVE_CLASS_PARTS = [
  'cancel',
  'close',
  'tab',
  'toggle',
  'switch',
  'menu',
  'sidebar',
  'header-icon',
  'theme'
];

function isGuardableAction(element) {
  if (!element || element.closest('[data-click-guard="off"]')) {
    return false;
  }
  if (element.disabled || element.getAttribute('aria-disabled') === 'true') {
    return false;
  }

  const tagName = element.tagName.toLowerCase();
  const type = (element.getAttribute('type') || '').toLowerCase();
  const className = String(element.className || '').toLowerCase();

  if (element.dataset.clickGuard === 'on') {
    return true;
  }
  if (element.dataset.clickGuard === 'off') {
    return false;
  }
  if (tagName === 'button' && type === 'submit') {
    return true;
  }
  if (PASSIVE_BUTTON_TYPES.has(type) && PASSIVE_CLASS_PARTS.some((part) => className.includes(part))) {
    return false;
  }
  if (tagName === 'a' && element.getAttribute('href')) {
    return false;
  }

  return (
    className.includes('btn-save') ||
    className.includes('btn-delete') ||
    className.includes('btn-disable') ||
    className.includes('btn-enable') ||
    className.includes('btn-paid') ||
    className.includes('btn-cash') ||
    className.includes('btn-alert') ||
    className.includes('btn-primary-action') ||
    className.includes('action-button') ||
    element.closest('form')
  );
}

function releaseButton(element) {
  element.classList.remove('is-click-guarded');
  element.removeAttribute('aria-busy');
  delete element.dataset.clickGuardBusy;
}

function getActionElement(target) {
  if (!target || typeof target.closest !== 'function') {
    return null;
  }
  return target.closest(ACTION_BUTTON_SELECTOR);
}

export default function AppInteractionGuard() {
  useEffect(() => {
    const timers = new Set();
    const guardedElements = new Set();

    const releaseAll = () => {
      guardedElements.forEach((element) => releaseButton(element));
      guardedElements.clear();
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };

    const onClickCapture = (event) => {
      const element = getActionElement(event.target);
      if (!isGuardableAction(element)) {
        return;
      }

      if (element.dataset.clickGuardBusy === 'true') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      element.dataset.clickGuardBusy = 'true';
      element.classList.add('is-click-guarded');
      element.setAttribute('aria-busy', 'true');
      guardedElements.add(element);

      const timer = window.setTimeout(() => {
        releaseButton(element);
        guardedElements.delete(element);
        timers.delete(timer);
      }, CLICK_GUARD_MS);
      timers.add(timer);
    };

    const onPointerUpCapture = (event) => {
      const element = getActionElement(event.target);
      if (element && element.classList.contains('is-click-guarded')) {
        element.classList.remove('is-pressing');
      }
    };

    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('pointerup', onPointerUpCapture, true);
    document.addEventListener('pointercancel', onPointerUpCapture, true);
    window.addEventListener('beforeunload', releaseAll);
    window.addEventListener('pagehide', releaseAll);

    return () => {
      document.removeEventListener('click', onClickCapture, true);
      document.removeEventListener('pointerup', onPointerUpCapture, true);
      document.removeEventListener('pointercancel', onPointerUpCapture, true);
      window.removeEventListener('beforeunload', releaseAll);
      window.removeEventListener('pagehide', releaseAll);
      releaseAll();
    };
  }, []);

  return null;
}
